/**
 * Deal calculator field specs (frontend-safe, exported as JS).
 * Each entry: sections[{title, fields:[{key,label,type,unit,default,help}]}].
 */
export const DEAL_TYPES = [
    { key: "fix_flip", label: "Fix and Flip", tagline: "Buy → renovate → sell. Short-hold value-add." },
    { key: "rental", label: "Rental / Buy-and-Hold", tagline: "Long-hold cash-flowing rental. BRRRR-ready." },
    { key: "ground_up_residential", label: "Ground-up Residential", tagline: "New construction custom, spec, or multi-family." },
    { key: "commercial", label: "Commercial", tagline: "Office, retail, industrial, mixed-use." },
    { key: "land", label: "Land / Subdivision", tagline: "Raw land through horizontal + lot sales." },
    { key: "tax_deed", label: "Tax-Deed / Foreclosure", tagline: "Auction acquisitions with rehab or resale." },
    { key: "tax_lien", label: "Tax-Lien Certificate", tagline: "Redemption yield with foreclosure fallback." },
];

const money = (label, key, def = 0, help) => ({ key, label, type: "money", default: def, help });
const pct = (label, key, def = 0, help) => ({ key, label, type: "percent", default: def, help });
const num = (label, key, def = 0, help) => ({ key, label, type: "number", default: def, help });
const text = (label, key, def = "", help) => ({ key, label, type: "text", default: def, help });

export const DEAL_SPECS = {
    fix_flip: [
        { title: "Acquisition", fields: [
            money("Purchase price", "purchase_price", 150000),
            money("Closing costs", "closing_costs", 3000),
            pct("MAO rule (%)", "mao_rule_pct", 70, "70% rule default; adjust for hot markets"),
        ]},
        { title: "Renovation", fields: [
            money("Rehab budget", "rehab_budget", 40000),
            pct("Contingency %", "contingency_pct", 10),
        ]},
        { title: "Financing", fields: [
            money("Loan amount", "loan_amount", 120000),
            pct("Financing rate (APR)", "financing_rate_pct", 10),
        ]},
        { title: "Holding & Sale", fields: [
            num("Holding period (months)", "holding_months", 6),
            money("Monthly holding costs", "monthly_holding_costs", 500),
            money("After-repair value (ARV)", "arv", 260000),
            pct("Selling costs %", "selling_costs_pct", 6),
        ]},
    ],
    rental: [
        { title: "Acquisition", fields: [
            money("Purchase price", "purchase_price", 250000),
            money("Closing costs", "closing_costs", 5000),
            money("Initial rehab", "initial_rehab", 10000),
        ]},
        { title: "Financing", fields: [
            money("Down payment", "down_payment", 62500),
            money("Loan amount", "loan_amount", 187500),
            pct("Interest rate", "interest_rate_pct", 7),
            num("Amortization years", "amortization_years", 30),
        ]},
        { title: "Revenue", fields: [
            money("Monthly rent", "monthly_rent", 2400),
            pct("Vacancy %", "vacancy_pct", 5),
        ]},
        { title: "Operating Expenses", fields: [
            money("Property tax (annual)", "property_tax_annual", 3000),
            money("Insurance (annual)", "insurance_annual", 1200),
            money("HOA (monthly)", "hoa_monthly", 0),
            pct("Maintenance %", "maintenance_pct", 8),
            pct("Management %", "management_pct", 8),
            money("Other (annual)", "other_annual", 500),
        ]},
        { title: "Growth Assumptions", fields: [
            pct("Rent growth %", "rent_growth_pct", 3),
            pct("Expense growth %", "expense_growth_pct", 2.5),
            pct("Appreciation %", "appreciation_pct", 3),
        ]},
    ],
    ground_up_residential: [
        { title: "Land & Site", fields: [
            money("Land cost", "land_cost", 100000),
            money("Site development", "site_dev_cost", 30000),
        ]},
        { title: "Construction (Hard)", fields: [
            money("Hard costs", "hard_costs", 250000),
            pct("GC OH %", "gc_oh_pct", 8),
            pct("GC profit %", "gc_profit_pct", 8),
            pct("Contingency %", "contingency_pct", 10),
        ]},
        { title: "Soft Costs", fields: [
            money("Soft costs", "soft_costs", 30000),
            money("Permits + fees", "permits_fees", 15000),
            pct("Developer fee %", "developer_fee_pct", 4),
        ]},
        { title: "Financing", fields: [
            money("Loan amount", "loan_amount", 400000),
            money("Financing costs", "financing_costs", 20000),
            money("Carrying costs", "carrying_costs", 8000),
        ]},
        { title: "Sale", fields: [
            num("Building sqft", "building_sqft", 2500),
            money("Expected sale price", "expected_sale_price", 650000),
            money("Marketing & sales", "marketing_sales", 12000),
            num("Duration (months)", "duration_months", 12),
        ]},
    ],
    commercial: [
        { title: "Acquisition", fields: [
            money("Purchase price", "purchase_price", 2000000),
            money("Renovation", "renovation", 200000),
            money("Tenant improvements", "tenant_improvements", 100000),
            money("Leasing commissions", "leasing_commissions", 50000),
            num("Free rent (months)", "free_rent_months", 2),
        ]},
        { title: "Property", fields: [
            num("Total sqft", "total_sqft", 20000),
            pct("Stabilized occupancy %", "stabilized_occupancy_pct", 95),
        ]},
        { title: "Revenue", fields: [
            money("Base rent PSF", "base_rent_psf", 24),
            money("Additional rent PSF (NNN)", "additional_rent_psf", 8),
            money("OpEx PSF", "opex_psf", 8),
        ]},
        { title: "Financing", fields: [
            money("Loan amount", "loan_amount", 1500000),
            pct("Interest rate", "interest_rate_pct", 7),
            num("Amortization years", "amortization_years", 30),
        ]},
        { title: "Exit", fields: [
            pct("Exit cap rate %", "exit_cap_rate_pct", 7),
            num("Hold years", "hold_years", 5),
            pct("Rent growth %", "rent_growth_pct", 3),
            pct("Expense growth %", "expense_growth_pct", 2),
        ]},
    ],
    land: [
        { title: "Land", fields: [
            money("Land acquisition", "land_acquisition", 500000),
            money("Entitlements", "entitlements", 50000),
            money("Engineering", "engineering", 80000),
            money("Platting", "platting", 20000),
            money("Impact fees", "impact_fees", 60000),
        ]},
        { title: "Horizontal", fields: [
            money("Roads", "roads", 300000),
            money("Drainage", "drainage", 100000),
            money("Utilities", "utilities", 200000),
            money("Site work", "site_work", 150000),
            money("Amenities", "amenities", 40000),
        ]},
        { title: "Financing", fields: [
            money("Loan amount", "loan_amount", 800000),
            money("Financing", "financing", 40000),
            money("Carrying", "carrying", 20000),
        ]},
        { title: "Sales", fields: [
            num("Number of lots", "number_of_lots", 40),
            money("Price per lot", "price_per_lot", 60000),
            num("Absorption (months / lot)", "absorption_months_per_lot", 1),
        ]},
    ],
    tax_deed: [
        { title: "Auction", fields: [
            money("Winning bid", "winning_bid", 20000),
            money("Buyer premium", "buyer_premium", 1000),
            money("Auction fees", "auction_fees", 500),
            money("Back taxes", "back_taxes", 5000),
        ]},
        { title: "Legal & Title", fields: [
            money("Legal", "legal", 3000),
            money("Title clearing", "title_clearing", 2000),
            money("Quiet title", "quiet_title", 3000),
            money("Eviction", "eviction", 1500),
        ]},
        { title: "Rehab & Holding", fields: [
            money("Rehab", "rehab", 25000),
            money("Security", "security", 500),
            money("Holding", "holding", 2000),
            money("Insurance", "insurance", 800),
            money("Utilities", "utilities", 500),
            num("Hold months", "hold_months", 8),
        ]},
        { title: "Exit", fields: [
            text("Exit strategy", "exit_strategy", "resale", "resale or rent"),
            money("Expected resale value", "expected_resale_value", 120000),
            money("Expected monthly rent", "expected_rent_monthly", 0),
            money("Resale cost", "resale_cost", 3000),
            pct("Possession probability %", "possession_probability_pct", 80),
        ]},
    ],
    tax_lien: [
        { title: "Certificate", fields: [
            money("Certificate amount", "certificate_amount", 5000),
            money("Premium", "premium", 200),
            pct("Statutory rate %", "statutory_rate_pct", 12),
            pct("Bid-down rate %", "bid_down_rate_pct", 0),
            pct("Penalty %", "penalty_pct", 4),
        ]},
        { title: "Add-ons", fields: [
            money("Subsequent taxes", "subsequent_taxes", 500),
            money("Legal & servicing", "legal_servicing", 100),
        ]},
        { title: "Redemption Model", fields: [
            pct("Redemption probability %", "redemption_probability_pct", 90),
            num("Expected redemption (months)", "expected_redemption_months", 12),
        ]},
        { title: "Foreclosure Scenario", fields: [
            money("Foreclosure cost", "foreclosure_cost", 0),
            money("Foreclosure property value", "foreclosure_property_value", 0),
        ]},
    ],
};

export const KPI_LABELS = {
    fix_flip: [
        { key: "net_profit", label: "Net Profit", fmt: "money" },
        { key: "roi_pct", label: "ROI", fmt: "pct" },
        { key: "roic_pct", label: "ROIC", fmt: "pct" },
        { key: "annualized_roi_pct", label: "Annualized ROI", fmt: "pct" },
        { key: "mao", label: "Max Allowable Offer", fmt: "money" },
        { key: "break_even_resale", label: "Break-even Resale", fmt: "money" },
    ],
    rental: [
        { key: "cash_flow_monthly", label: "Cash Flow / mo", fmt: "money" },
        { key: "cap_rate_pct", label: "Cap Rate", fmt: "pct" },
        { key: "dscr", label: "DSCR", fmt: "ratio" },
        { key: "cash_on_cash_pct", label: "Cash-on-Cash", fmt: "pct" },
        { key: "break_even_occupancy_pct", label: "Break-even Occupancy", fmt: "pct" },
        { key: "noi", label: "NOI (annual)", fmt: "money" },
        { key: "break_even_rent_monthly", label: "Break-even Rent", fmt: "money" },
    ],
    ground_up_residential: [
        { key: "expected_profit", label: "Expected Profit", fmt: "money" },
        { key: "roc_pct", label: "ROC", fmt: "pct" },
        { key: "roe_pct", label: "ROE", fmt: "pct" },
        { key: "equity_multiple", label: "Equity Multiple", fmt: "ratio" },
        { key: "total_dev_cost", label: "Total Dev Cost", fmt: "money" },
        { key: "cost_per_sqft", label: "Cost / sqft", fmt: "money" },
    ],
    commercial: [
        { key: "noi", label: "NOI", fmt: "money" },
        { key: "cap_rate_pct", label: "Cap Rate", fmt: "pct" },
        { key: "dscr", label: "DSCR", fmt: "ratio" },
        { key: "irr_pct", label: "IRR (levered)", fmt: "pct" },
        { key: "equity_multiple", label: "Equity Multiple", fmt: "ratio" },
        { key: "stabilized_value", label: "Stabilized Value", fmt: "money" },
    ],
    land: [
        { key: "profit", label: "Profit", fmt: "money" },
        { key: "roc_pct", label: "ROC", fmt: "pct" },
        { key: "irr_pct", label: "IRR", fmt: "pct" },
        { key: "revenue", label: "Revenue", fmt: "money" },
        { key: "cost_per_lot", label: "Cost / lot", fmt: "money" },
        { key: "equity_multiple", label: "Equity Multiple", fmt: "ratio" },
    ],
    tax_deed: [
        { key: "expected_profit", label: "Expected Profit", fmt: "money" },
        { key: "roc_pct", label: "ROC", fmt: "pct" },
        { key: "probability_adjusted_profit", label: "Prob-Adj Profit", fmt: "money" },
        { key: "max_bid", label: "Max Bid", fmt: "money" },
        { key: "break_even_bid", label: "Break-even Bid", fmt: "money" },
        { key: "total_capital", label: "Total Capital", fmt: "money" },
    ],
    tax_lien: [
        { key: "annualized_yield_pct", label: "Annualized Yield", fmt: "pct" },
        { key: "net_interest", label: "Net Interest", fmt: "money" },
        { key: "cash_on_cash_pct", label: "Cash-on-Cash", fmt: "pct" },
        { key: "probability_adjusted_return_pct", label: "Prob-Adj Return", fmt: "pct" },
        { key: "gross_interest", label: "Gross Interest", fmt: "money" },
        { key: "invested_capital", label: "Invested Capital", fmt: "money" },
    ],
};
